import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range-ts";
import "react-date-range-ts/dist/styles.css";
import "react-date-range-ts/dist/theme/default.css";
import {
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  subDays,
} from "date-fns";
import { IoIosArrowDown } from "react-icons/io";
import { FiCalendar } from "react-icons/fi";

// import { getStartAndEndOfMonth } from "../../../../utils/getStartDateAndEndDate";

export const formatDate = (dateString: Date) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const PAYOUT_DROPDOWN_OPEN_EVENT = "payout-dropdown-open";

function TransactionDateFilter({
  setType,
  type,
  refetch,
  selectedRange,
  setSelectedRange,
  setIsDateRangeIsSelected,
  displayText,
  popoverPlacement,
  minWidthClassName,
}: any) {
  const [openDateRange, setOpenDateRange] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [alignPopoverRight, setAlignPopoverRight] = useState(false);
  const [popoverMaxHeight, setPopoverMaxHeight] = useState(520);

  // const { startDate, endDate } = getStartAndEndOfMonth();
  const divRef = useRef<HTMLDivElement>(null);
  const dateFilterIdRef = useRef(
    `transaction-date-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  const calculatePopoverPosition = useCallback(() => {
    const rect = divRef.current?.getBoundingClientRect();
    if (!rect) return;

    const viewportPadding = 16;
    const minimumUsableHeight = 430;
    const popoverWidth = Math.min(520, window.innerWidth - viewportPadding * 2);
    setAlignPopoverRight(rect.left + popoverWidth > window.innerWidth - viewportPadding);

    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    if (popoverPlacement === "down") {
      setOpenUpward(false);
      setPopoverMaxHeight(Math.max(minimumUsableHeight, Math.min(520, spaceBelow)));
      return;
    }
    const shouldOpenUpward = spaceBelow < 520 && spaceAbove > spaceBelow;
    const availableSpace = shouldOpenUpward ? spaceAbove : spaceBelow;

    setOpenUpward(shouldOpenUpward);
    setPopoverMaxHeight(Math.max(minimumUsableHeight, Math.min(520, availableSpace)));
  }, [popoverPlacement]);

  const handleOutsideClick = (event: any) => {
    if (divRef.current && !divRef.current.contains(event.target)) {
      setOpenDateRange(false);
    }
  };

  useEffect(() => {
    const handleOtherDropdownOpen = (event: Event) => {
      const openedDropdownId = (event as CustomEvent<string>).detail;
      if (openedDropdownId !== dateFilterIdRef.current) {
        setOpenDateRange(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener(PAYOUT_DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
    window.addEventListener("resize", calculatePopoverPosition);
    window.addEventListener("scroll", calculatePopoverPosition, true);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener(
        PAYOUT_DROPDOWN_OPEN_EVENT,
        handleOtherDropdownOpen,
      );
      window.removeEventListener("resize", calculatePopoverPosition);
      window.removeEventListener("scroll", calculatePopoverPosition, true);
    };
  }, [calculatePopoverPosition]);
  const handlePresetFilter = (type: any) => {
    let startDate, endDate;
    setIsDateRangeIsSelected(true);
    switch (type) {
      case "today":
        startDate = startOfDay(new Date());
        endDate = endOfDay(new Date());

        setType("Today");
        break;
      case "last7days":
        startDate = subDays(new Date(), 7);
        endDate = new Date();

        setType("Last 7 days");
        break;
      case "thisMonth":
        startDate = startOfMonth(new Date());
        endDate = endOfMonth(new Date());
        setType("This Month");

        break;
      case "lastMonth":
        startDate = startOfMonth(subDays(new Date(), 30));
        endDate = endOfMonth(subDays(new Date(), 30));
        setType("Last Month");
        break;
      default:
        startDate = new Date();
        endDate = new Date();
        break;
    }

    setSelectedRange({
      startDate,
      endDate,
      key: "selection",
    });
  };

  const clearSelection = async () => {
    setIsDateRangeIsSelected(false);
    setSelectedRange({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
      key: "selection",
    });
    // refetch({
    //   start_date: startDate,
    //   end_date: endDate,
    // });

    setType("");
  };

  return (
    <div className={"relative w-full " + (minWidthClassName ?? "min-w-[228px]")} ref={divRef}>
      <button
        type="button"
        onClick={() => {
          if (!openDateRange) {
            calculatePopoverPosition();
            document.dispatchEvent(
              new CustomEvent(PAYOUT_DROPDOWN_OPEN_EVENT, {
                detail: dateFilterIdRef.current,
              }),
            );
          }
          setOpenDateRange((current) => !current);
        }}
        className={
          "flex h-10 w-full items-center rounded-[6px] border bg-white text-left text-sm font-medium text-[#1B163B] transition-all duration-150 focus:outline-none " +
          (openDateRange ? "border-[#6687FF]" : "border-[#E0E5F2]")
        }
      >
        <span className="mr-auto flex min-w-0 items-center gap-2 pl-3">
          {displayText && <FiCalendar className="shrink-0 text-[#505E8E]" />}
          <span className="truncate">
          {displayText ?? (
            <>
              Date{" "}
              {type === "Today" && (
                <span>(Today)</span>
              )}
            </>
          )}
          </span>
        </span>
        <IoIosArrowDown
          className={
            "w-8 text-sm text-[#1E1B59] transition-transform " +
            (openDateRange ? "rotate-180" : "")
          }
        />
      </button>
      {openDateRange && (
        <div
          style={{ maxHeight: popoverMaxHeight }}
          className={
            "statement-date-popover absolute z-50 flex w-[520px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[6px] bg-white text-sm text-[#1B163B] shadow-[0_12px_32px_rgba(27,22,59,0.14)] " +
            (alignPopoverRight ? "right-0 " : "left-0 ") +
            (openUpward
              ? "bottom-[calc(100%+8px)]"
              : "top-[calc(100%+2px)]")
          }
        >
          <div className="shrink-0 px-8 pt-4">
            <p className="text-center text-base font-medium">Custom Range</p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-8">
            <div className="mt-5 grid shrink-0 grid-cols-2 gap-x-16 gap-y-5 text-sm font-medium">
              <button
                type="button"
                className="cursor-pointer rounded-md text-left hover:text-[#6687FF]"
                onClick={() => handlePresetFilter("today")}
              >
                Today
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-md text-left hover:text-[#6687FF]"
                onClick={() => handlePresetFilter("last7days")}
              >
                Last 7 Days
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-md text-left hover:text-[#6687FF]"
                onClick={() => handlePresetFilter("thisMonth")}
              >
                This Month
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-md text-left hover:text-[#6687FF]"
                onClick={() => handlePresetFilter("lastMonth")}
              >
                Last Month
              </button>
            </div>

            <div className="mt-5 flex justify-center">
              <DateRange
                ranges={[selectedRange]}
                onChange={(item: any) => {
                  setSelectedRange(item.selection);
                  setIsDateRangeIsSelected(true);
                }}
                maxDate={new Date()}
                showDateDisplay={false}
              />
            </div>
          </div>
          <div className="shrink-0 border-t border-[#E0E5F2] bg-white px-8 py-3">
            <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="h-10 rounded-[8px] border border-[#E0E5F2] px-5 text-sm font-semibold text-[#6687FF]"
              onClick={clearSelection}
            >
              Clear
            </button>
            <button
              type="button"
              // disabled={
              //   selectedRange.startDate.getDate() === new Date().getDate()
              // }
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="h-10 rounded-[8px] px-6 text-sm font-semibold disabled:bg-gray-100 disabled:text-gray-500"
              onClick={async () => {
                refetch();
                setType("Custom Date");
                setOpenDateRange(false);
              }}
            >
              Apply
            </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionDateFilter;
